import type { Comic } from '@tanko/shared'
import { CloudReadingStore } from '../db/cloud-reading-repo'
import { clearBookmarkPush, clearProgressPush, unpushedReadingState } from '../db/reading-repo'
import { CloudApiClient } from './api-client'
import { backupFailureKind } from './backup-failure'
import { errorText, type BulkOutcome } from './bulk-progress'
import { isRegistered } from './comic-sync'

/**
 * 把裝置上還沒送出去的閱讀狀態補到雲端。
 *
 * 存在的理由是那些被關在裝置裡的資料：升級之前雲端只收已備份作品的進度與書籤，因此在
 * 本地讀過但還沒傳完的作品，讀到哪裡、加了什麼書籤全部只有這一台機器知道。升級時那些
 * 紀錄一律被標為待推送，這裡負責把它們送上去。
 */

export type PushTarget = { comicId: string; cloudComicId: string; pageCount: number }

/** 補送的結果。跳過的那些不是成功，但也不該擋住其他作品。 */
export type PushOutcome = BulkOutcome & { skipped: { comicId: string; reason: string }[] }

/**
 * 挑出送得出去的作品。
 *
 * 只有已註冊的作品有目錄項可以掛——未註冊的留著標記，等註冊完成的那一輪再送。判準是
 * syncState 而不是 cloudComicId 有沒有值：識別碼在註冊送出之前就先配發了。
 */
export function pushTargets(comics: readonly Comic[], pending: readonly string[]): PushTarget[] {
  const waiting = new Set(pending)
  return comics
    .filter(comic => waiting.has(comic.id) && !!comic.cloudComicId && isRegistered(comic))
    .map(comic => ({ comicId: comic.id, cloudComicId: comic.cloudComicId!, pageCount: comic.pageCount }))
}

/**
 * 逐本補送。
 *
 * 失敗的分流沿用備份佇列那一套：系統性錯誤（登入過期、伺服器 5xx）在下一本身上會一模
 * 一樣地再發生，因此停整批；單本的錯誤只跳過那一本。補送是背景工作，沒有人在等它——
 * 一律遇錯即停的話，一本永遠推不出去的作品會無限期擋住其他每一本的閱讀狀態。
 */
export async function runReadingPush(
  targets: readonly PushTarget[], push: (target: PushTarget) => Promise<void>,
): Promise<PushOutcome> {
  const completedIds: string[] = []
  const skipped: { comicId: string; reason: string }[] = []
  for (const target of targets) {
    try { await push(target) }
    catch (cause) {
      if (backupFailureKind(cause) === 'systemic') {
        return { completed: completedIds.length, completedIds, reason: errorText(cause), cause, skipped }
      }
      skipped.push({ comicId: target.comicId, reason: errorText(cause) })
      continue
    }
    completedIds.push(target.comicId)
  }
  return { completed: completedIds.length, completedIds, reason: '', skipped }
}

/**
 * 送出一本作品待推送的進度與書籤。
 *
 * 伺服器受理（含輸掉 last-write-wins 被拒）就清掉標記；連不上或登入過期會拋錯，標記
 * 留著等下一輪。tombstone 走刪除端點，清標記時那一列才真的被移除。
 */
export async function pushComicReadingState(target: PushTarget, api = new CloudApiClient()): Promise<void> {
  const { progress, bookmarks } = await unpushedReadingState(target.comicId)
  if (!progress && !bookmarks.length) return
  const cloud = new CloudReadingStore(target.cloudComicId, target.pageCount, api)
  if (progress) {
    await cloud.saveProgress(progress)
    await clearProgressPush(target.comicId)
  }
  for (const bookmark of bookmarks) {
    if (bookmark.deleted) await cloud.deleteBookmark(bookmark.id)
    else await cloud.putBookmark({
      id: bookmark.id, page: bookmark.page, offset: bookmark.offset,
      label: bookmark.label, createdAt: bookmark.createdAt, updatedAt: bookmark.updatedAt,
    })
    await clearBookmarkPush(target.comicId, bookmark.id)
  }
}
